import React from 'react';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import StatCard, { Delta } from './StatCard';
import { formatEur } from '@/lib/writer/telemetryCost';
import {
  compileHealthTone,
  type DaySeriesPoint,
  type RangeTotals,
} from '@/lib/writer/telemetryTypes';

interface Props {
  series: DaySeriesPoint[];
  totals: RangeTotals;
  previous: RangeTotals | null;
  days: number;
}

const Spark: React.FC<{ data: DaySeriesPoint[]; pick: (p: DaySeriesPoint) => number }> = ({
  data,
  pick,
}) => (
  <div className="h-8 -mb-1 mt-2">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data.map((p) => ({ v: pick(p) }))}>
        <Line
          type="monotone"
          dataKey="v"
          stroke="hsl(var(--primary))"
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const HealthStrip: React.FC<Props> = ({ series, totals, previous, days }) => {
  const rate = totals.compileSuccessRate;
  const perDay = (n: number) => (days > 0 ? n / days : n);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <StatCard
        label="Compile success"
        tone={compileHealthTone(rate)}
        value={rate === null ? '—' : `${(rate * 100).toFixed(0)}%`}
        hint={
          <>
            {totals.compilesSuccess}/{totals.compiles} compiles
            <Spark data={series} pick={(p) => (p.compileSuccessRate ?? 0) * 100} />
          </>
        }
      />
      <StatCard
        label="Compiles / day"
        value={perDay(totals.compiles).toFixed(1)}
        hint={
          <>
            {previous ? <Delta current={totals.compiles} previous={previous.compiles} /> : '—'}
            <Spark data={series} pick={(p) => p.compilesSuccess + p.compilesFailed} />
          </>
        }
      />
      <StatCard
        label="Turns / day"
        value={perDay(totals.turns).toFixed(1)}
        hint={
          <>
            {previous ? <Delta current={totals.turns} previous={previous.turns} /> : '—'}
            <Spark data={series} pick={(p) => p.turns} />
          </>
        }
      />
      <StatCard
        label="Cost / day"
        value={formatEur(perDay(totals.costEur))}
        tone={perDay(totals.costEur) > 5 ? 'warn' : 'ok'}
        hint={
          <>
            {formatEur(totals.costEur)} in range
            {totals.costPartial && ' (partial: unpriced model)'}
            <Spark data={series} pick={(p) => p.costEur} />
          </>
        }
      />
      <StatCard
        label="Active users"
        value={totals.activeUsers.toString()}
        hint={
          <>
            {totals.turns} turns total
            <Spark data={series} pick={(p) => p.activeUsers} />
          </>
        }
      />
    </div>
  );
};

export default HealthStrip;
